import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAppStore } from '@/store';
import { cn } from '@/lib/utils';
import type { PlanItem } from '@/services/api';
import {
  X, FileText, MessageSquarePlus, Trash2, Loader2, Check, Sparkles, FileCheck2,
} from 'lucide-react';

const EMPTY_PLAN: PlanItem[] = [];

export function PreviewPanel() {
  const previewOpen = useAppStore((s) => s.previewOpen);
  const previewBody = useAppStore((s) => s.previewBody);
  const previewLoading = useAppStore((s) => s.previewLoading);
  const previewComments = useAppStore((s) => s.previewComments);
  const previewTemplate = useAppStore((s) => s.previewTemplate);
  const closePreview = useAppStore((s) => s.closePreview);
  const addPreviewComment = useAppStore((s) => s.addPreviewComment);
  const removePreviewComment = useAppStore((s) => s.removePreviewComment);
  const refineWithComments = useAppStore((s) => s.refineWithComments);
  const finalizeFromPreview = useAppStore((s) => s.finalizeFromPreview);
  const previewDocumentId = useAppStore((s) => s.previewDocumentId);
  const activeTool = useAppStore((s) => s.activeTool);
  const documentPlan = useAppStore((s) => s.activeSession?.documentPlan);
  const planList = documentPlan ?? EMPTY_PLAN;
  const currentItem = planList.find((p) => p.id === previewDocumentId) || null;
  const currentIdx = currentItem ? planList.findIndex((p) => p.id === currentItem.id) + 1 : 0;

  const previewRef = useRef<HTMLDivElement>(null);
  const [floater, setFloater] = useState<{ x: number; y: number; text: string } | null>(null);
  const [draftCommentText, setDraftCommentText] = useState<string | null>(null);
  const [draftSelection, setDraftSelection] = useState<string>('');
  const [draftValue, setDraftValue] = useState('');

  // Listen for selection within preview content
  useEffect(() => {
    if (!previewOpen) return;
    const handler = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) {
        if (!draftCommentText) setFloater(null);
        return;
      }
      const text = sel.toString().trim();
      if (text.length < 3) {
        if (!draftCommentText) setFloater(null);
        return;
      }
      const range = sel.getRangeAt(0);
      const container = previewRef.current;
      if (!container) return;
      // Only consider selections inside the preview container
      if (!container.contains(range.commonAncestorContainer)) return;

      const rect = range.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      setFloater({
        x: rect.right - containerRect.left + 4,
        y: rect.top - containerRect.top + container.scrollTop,
        text,
      });
    };
    document.addEventListener('mouseup', handler);
    document.addEventListener('keyup', handler);
    return () => {
      document.removeEventListener('mouseup', handler);
      document.removeEventListener('keyup', handler);
    };
  }, [previewOpen, draftCommentText]);

  if (!previewOpen) return null;

  const startCommentDraft = () => {
    if (!floater) return;
    setDraftSelection(floater.text);
    setDraftCommentText(floater.text);
    setDraftValue('');
    setFloater(null);
    window.getSelection()?.removeAllRanges();
  };

  const saveComment = () => {
    if (!draftCommentText || !draftValue.trim()) return;
    addPreviewComment(draftSelection, draftValue);
    setDraftCommentText(null);
    setDraftSelection('');
    setDraftValue('');
  };

  const cancelDraft = () => {
    setDraftCommentText(null);
    setDraftSelection('');
    setDraftValue('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm sm:p-4">
      <div className="w-full max-w-7xl h-full sm:h-[92vh] bg-[#0c0c0e] border-0 sm:border sm:border-white/10 sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-14 border-b border-white/10 px-3 sm:px-5 flex items-center justify-between shrink-0 gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <FileText className="h-5 w-5 text-blue-400 shrink-0" />
            <h2 className="text-sm font-semibold text-white shrink-0">
              Попередній перегляд
              {currentIdx > 0 && (
                <span className="ml-2 text-white/40 font-normal">
                  ({currentIdx} з {documentPlan.length})
                </span>
              )}
            </h2>
            {previewTemplate && (
              <span className="px-2 py-0.5 rounded bg-white/5 border border-white/10 text-[10px] text-white/50 uppercase truncate hidden sm:inline">
                {previewTemplate}
              </span>
            )}
            <span className="text-xs text-white/30 hidden md:inline truncate">
              Виділіть текст у документі, щоб залишити коментар
            </span>
          </div>
          <button
            onClick={closePreview}
            disabled={previewLoading}
            className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 flex flex-col xl:flex-row min-h-0">
          {/* Document preview */}
          <div className="flex-1 min-w-0 min-h-0 relative bg-[#111114]">
            {previewLoading && previewBody === '' ? (
              <div className="h-full flex flex-col items-center justify-center gap-3 text-white/40">
                <Loader2 className="h-6 w-6 animate-spin" />
                {activeTool ? (
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-xs text-purple-400/80 font-mono">🔧 {activeTool}</span>
                    <span className="text-sm">Генерую через doc-writer MCP…</span>
                  </div>
                ) : (
                  <span className="text-sm">Готую попередній перегляд…</span>
                )}
              </div>
            ) : (
              <>
                <div
                  ref={previewRef}
                  className="h-full overflow-y-auto px-3 sm:px-8 lg:px-12 py-6 lg:py-10 relative"
                >
                  <div className="preview-paper markdown-body max-w-3xl mx-auto bg-white rounded-md shadow-2xl px-5 py-6 sm:px-10 sm:py-10 lg:px-14 lg:py-12 select-text">
                    <ReactMarkdown>{previewBody}</ReactMarkdown>
                  </div>

                  {/* Floating "add comment" tooltip */}
                  {floater && (
                    <button
                      onClick={startCommentDraft}
                      style={{ left: floater.x, top: floater.y }}
                      className="absolute z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs shadow-lg shadow-black/60 transition-colors animate-in fade-in slide-in-from-left-2"
                    >
                      <MessageSquarePlus className="h-3.5 w-3.5" />
                      Коментувати
                    </button>
                  )}
                </div>

                {previewLoading && previewBody !== '' && (
                  <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-3 text-white">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="text-sm">Оновлюю текст з вашими зауваженнями…</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Comments sidebar */}
          <aside className="w-full xl:w-80 max-h-[40vh] xl:max-h-none border-t xl:border-t-0 xl:border-l border-white/10 flex flex-col min-h-0 bg-[#0e0e10]">
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
              <MessageSquarePlus className="h-4 w-4 text-blue-400" />
              <span className="text-sm font-semibold text-white">Зауваження</span>
              <span className="text-xs text-white/30">({previewComments.length})</span>
            </div>

            {/* Draft */}
            {draftCommentText && (
              <div className="p-4 border-b border-white/10 bg-blue-500/5">
                <div className="text-[10px] uppercase text-white/30 mb-1.5">Виділений фрагмент</div>
                <div className="text-xs text-white/70 italic bg-white/5 border-l-2 border-blue-500 pl-2 py-1 mb-3 max-h-24 overflow-y-auto">
                  «{draftSelection}»
                </div>
                <textarea
                  autoFocus
                  value={draftValue}
                  onChange={(e) => setDraftValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') cancelDraft();
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) saveComment();
                  }}
                  placeholder="Що змінити в цьому фрагменті?"
                  rows={3}
                  className="w-full bg-black/30 border border-white/10 rounded-lg p-2 text-xs text-white leading-relaxed resize-none focus:outline-none focus:border-blue-500/50 min-h-[70px]"
                />
                <div className="flex justify-end gap-1.5 mt-2">
                  <button
                    onClick={cancelDraft}
                    className="px-2.5 py-1 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-[11px] text-white/60 transition-colors flex items-center gap-1"
                  >
                    <X className="h-3 w-3" /> Скасувати
                  </button>
                  <button
                    onClick={saveComment}
                    disabled={!draftValue.trim()}
                    className="px-2.5 py-1 rounded-md bg-blue-600 hover:bg-blue-500 text-[11px] text-white transition-colors disabled:opacity-40 flex items-center gap-1"
                  >
                    <Check className="h-3 w-3" /> Додати
                  </button>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {previewComments.length === 0 && !draftCommentText && (
                <div className="text-center py-8 text-white/30 text-xs px-4">
                  Виділіть текст у документі лівою кнопкою миші — зʼявиться кнопка «Коментувати».
                </div>
              )}
              {previewComments.map((c, i) => (
                <div key={c.id} className="bg-white/5 border border-white/10 rounded-lg p-3">
                  <div className="flex items-start justify-between mb-1.5">
                    <span className="text-[10px] uppercase text-white/30">Зауваження {i + 1}</span>
                    <button
                      onClick={() => removePreviewComment(c.id)}
                      className="p-1 text-white/30 hover:text-red-400 transition-colors"
                      title="Видалити"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="text-[11px] text-white/50 italic border-l-2 border-blue-500/40 pl-2 py-0.5 mb-2 line-clamp-3">
                    «{c.text}»
                  </div>
                  <div className="text-xs text-white/80 leading-relaxed whitespace-pre-wrap">
                    {c.comment}
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>

        {/* Footer buttons */}
        <footer className="border-t border-white/10 px-4 sm:px-5 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 shrink-0 bg-[#0a0a0c]">
          <div className="text-xs text-white/40 hidden sm:block">
            {previewComments.length > 0
              ? `${previewComments.length} зауваж${previewComments.length === 1 ? 'ення' : 'ень'} — натисніть «Звернути увагу», щоб LLM врахував їх.`
              : 'Якщо все влаштовує — натисніть «Генерувати документ».'}
          </div>
          <div className="flex items-center gap-2 justify-end">
            {previewComments.length > 0 && (
              <button
                onClick={refineWithComments}
                disabled={previewLoading}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-600/20 hover:bg-amber-600/30 border border-amber-500/40 text-amber-300 font-medium text-sm transition-colors disabled:opacity-40"
              >
                {previewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Звернути увагу за зауваження
              </button>
            )}
            <button
              onClick={finalizeFromPreview}
              disabled={previewLoading || !previewBody}
              className={cn(
                'flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-colors disabled:opacity-40',
                'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-900/40'
              )}
            >
              {previewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileCheck2 className="h-4 w-4" />}
              Генерувати документ
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
